/**
 *
 * @description ConfirmDialog.tsx
 * @author yikkok <yikok.yong@gmail.com>
 * @version 1.0.0
 * @since 13 November 2021
 *
 */

import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
  message: string;
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
};
export default function ConfirmDialog({ open, onClose, onConfirm, onCancel, message, title }: Props) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as='div' className='fixed inset-0 z-10 overflow-y-auto bg-gray-700 bg-opacity-80' onClose={onClose}>
        <div className='min-h-screen px-4 text-center'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0' />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className='inline-block h-screen align-middle' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 scale-95'
            enterTo='opacity-100 scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 scale-100'
            leaveTo='opacity-0 scale-95'
          >
            <div className='inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl'>
              <Dialog.Title as='h3' className='text-lg font-medium leading-6 text-gray-900'>
                {title}
              </Dialog.Title>
              <div className='mt-2'>
                <p className='text-sm text-gray-500'>{message}</p>
              </div>

              <div className='mt-4 flex justify-end'>
                <button
                  type='button'
                  className='inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 mr-2'
                  onClick={onCancel}
                >
                  No
                </button>
                <button
                  type='button'
                  className='inline-flex justify-center px-4 py-2 text-sm font-medium text-black bg-main-300 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500'
                  onClick={onConfirm}
                >
                  Yes
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
